/**
 * Token utility functions for JWT parsing and token management
 */

/**
 * Decode JWT token
 * @param {string} token - JWT token to decode
 * @returns {Object|null} - Decoded token payload or null if invalid
 */
export const parseJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (error) {
        // console.error('Error parsing JWT:', error);
        return null;
    }
};

/**
 * Calculate NGCMFA expiration using token iat + specified minutes
 * @param {Object} decodedToken - Decoded JWT token
 * @param {number} expiryMinutes - Minutes to add to iat (default: 10)
 * @param {number} secondsPerMinute - Seconds per minute (default: 60)
 * @returns {number|null} - Expiration timestamp or null if invalid
 */
export const calculateNgcmfaExpiration = (decodedToken, expiryMinutes = 10, secondsPerMinute = 60) => {
    if (decodedToken && decodedToken.iat) {
        const expiration = decodedToken.iat + (expiryMinutes * secondsPerMinute);
        // console.log('NGCMFA expiration calculated:', expiration, 'Token iat:', decodedToken.iat, 'Current time:', Math.floor(Date.now() / 1000));
        return expiration;
    }
    return null;
};

/**
 * Get access token from MSAL instance
 * @param {Object} instance - MSAL instance
 * @param {Array} accounts - User accounts
 * @param {Object} tokenRequest - Token request configuration
 * @returns {Promise<Object>} - Object containing { token, decodedToken, error }
 */
export const getAccessToken = async (instance, accounts, loginRequest) => {
    if (accounts.length > 0) {
        const request = {
            ...loginRequest,
            account: accounts[0],
        };
        
        // console.log("Accounts", accounts)

        try {
            // console.log('Account found for token request');
            const response = await instance.acquireTokenSilent(request);
            const decodedToken = parseJwt(response.accessToken);
            // console.log('Access token acquired successfully', response);

            return {
                token: response.accessToken,
                decodedToken: decodedToken,
                idToken: response.idToken,
                error: null
            };
        } catch (error) {
            // console.error('Error acquiring access token:', error);
            if (error.errorCode === 'invalid_grant' && error.message.includes('multi-factor authentication has expired')) {
                // Force interactive authentication for MFA expiry
                return await instance.acquireTokenRedirect(request);
            }
            return {
                token: null,
                decodedToken: null,
                error: 'Failed to acquire access token. This might be because the token is not available or has expired. Please re-sign in.'
            };
        }
    } else {
        // No accounts found - redirect to login
        try {
            await instance.loginRedirect(loginRequest);
            return {token: null, decodedToken: null, error: 'Redirecting to login...'};
        } catch (loginError) {
            return {token: null, decodedToken: null, error: 'No account found'};
        }
    }
};
